import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { Tooltip } from "primereact/tooltip";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { confirmDialog } from "primereact/confirmdialog";
import { Nullable } from "primereact/ts-helpers";
import { InputNumberChangeEvent } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { saveAs } from "file-saver";

import NoDataCreated from "../../../Components/NoDataCreated";
import ErrorPage from "../../../Components/ErrorPage";
import DatePicker from "../../../Components/DatePicker";
import NumberInput from "../../../Components/NumberInput";
import TextInput from "../../../Components/TextInput";
import PageLoader from "../../../Components/PageLoader";
import Textarea from "../../../Components/TextArea";
import { goToTop } from "../../../Components/goToTop";
import { formatDate, parseStringToDate } from "../../../Utils/getDate";
import { getAppCurrencyType } from "../../../Utils/Common";

import { useAppDispatch, useAppSelector } from "../../../Controllers/hooks";
import {
  getAllMaterialProjectList,
  getAllMaterialUnitList,
} from "../../../Controllers/Thunks/HeyTraderThunks";
import { MaterialPojectType } from "../../../Controllers/Types/HeyTraderTypes/Materials/MaterialPojectType";
import { MaterialType } from "../../../Controllers/Types/HeyTraderTypes/Materials/MaterialType";
import { MaterialEstimationType } from "../../../Controllers/Types/HeyTraderTypes/Materials/MaterialEstimationType";
import { MaterialOrderType } from "../../../Controllers/Types/HeyTraderTypes/Materials/MaterialOrderType";
import { MaterialUnitType } from "../../../Controllers/Types/HeyTraderTypes/Materials/MaterialUnitType";
import {
  addDeleverd,
  addMaterialItem,
  addOnsite,
  addOrder,
  deleteDeleverd,
  deleteMaterialItemData,
  deleteOnsite,
  deleteOrder,
  updateDeleverd,
  updateMaterialItem,
  updateOnsite,
  updateOrder,
  getMaterialResultData,
  getMaterialPurchaseOrderList,
  downloadMaterialPurchaseOrder,
} from "../../../Api/HeyTraderService/MaterialsService";
import {
  checkModelSelected,
  checkNumber,
  checkText,
} from "../../../Utils/Validator";
import { useSelector } from "react-redux";
import { hasPermission } from "../../../Controllers/Selectors/permissionsSelector";
import { RootState } from "../../../Controllers/Store";
import { MaterialDataType } from "../../../Controllers/Types/HeyTraderTypes/Materials/MaterialDataType";
import { AnimatePresence, motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { MaterialStructureType } from "../../../Controllers/Types/HeyTraderTypes/Materials/MaterialStructureType";
import { MaterialDealType } from "../../../Controllers/Types/HeyTraderTypes/Materials/MaterialDealType";
import { MaterialPurchaseOrderGroup } from "../../../Controllers/Types/HeyTraderTypes/Materials/MaterialPurchaseOrderType";
import "./Css/Materials.css";
import "./Css/Materials-responsive.css";

const MaterialItems: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useRef<Toast>(null);
  const currencySymbol = getAppCurrencyType();

  const [loading, setLoading] = useState<boolean>(false);
  const [dataState, setDataState] = useState<"Add" | "Edit">("Add");

  // view and common purpose
  const [projectDetails, setProjectDetails] = useState<MaterialPojectType | null>(null);

  const [itemViewState, setItemViewState] = useState<"Estimation" | "Order" | "Delivered" | "Onsite">("Estimation");
  const [materialDataUnitsList, setMaterialDataUnits] = useState<MaterialUnitType[]>([]);
  const [materialListData, setMaterialListData] = useState<MaterialType | null>(null);
  const [selectedItemData, setSelectedItemData] = useState<MaterialEstimationType | null>(null);
  const [showNoteViewModal, setShowNoteViewModal] = useState<boolean>(false);
  const [selectedItemNote, setSelectedItemNote] = useState<string>("");

  /* Material item */
  const [showMaterialItemModal, setShowMaterialItemModal] = useState<boolean>(false);

  const [itemName, setItemName] = useState<string>("");
  const [materialUnit, setMaterialUnit] = useState<MaterialUnitType | null>(null);
  const [quantity, setQuantity] = useState<number>(0);
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const [baseUnitPrice, setBaseUnitPrice] = useState(0);
  const [isIncludeTax, setIsIncludeTax] = useState<boolean>(false);
  const [subTotal, setSubTotal] = useState<number>(0);
  const [note, setNote] = useState<string>("");

  const round2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;

  const [itemNameError, setItemNameError] = useState<string>("");
  const [materialUnitError, setMaterialUnitError] = useState<string>("");
  const [quantityError, setQuantityError] = useState<string>("");
  const [unitPriceError, setUnitPriceError] = useState<string>("");
  const [subTotalError, setSubTotalError] = useState<string>("");
  const [noteError, setNoteError] = useState<string>("");

  /* Order */
  const [showOrderModal, setShowOrderModal] = useState<boolean>(false);
  const [orderQuantity, setOrderQuantity] = useState<number | null>(null);
  const [orderUnitPrice, setOrderUnitPrice] = useState<number | null>(null);
  const [orderUnitLastPrice, setOrderUnitLastPrice] = useState<number | null>(null);
  const [orderDate, setOrderDate] = useState<Nullable<Date>>(null);
  const [selectedOrderItemId, setSelectedOrderItemId] = useState<number | null>(null);
  const [showBestDealModal, setShowBestDealModal] = useState(false);
  const [dealLoading, setDealLoading] = useState(false);
  const [materialDeals, setMaterialDeals] = useState<MaterialDealType[]>([]);
  const [selectedDeal, setSelectedDeal] = useState<MaterialDealType | null>(null);
  const [dealSearch, setDealSearch] = useState("");
  const [selectedDealShop, setSelectedDealShop] = useState<string | null>(null);
  const [selectedDealBrand, setSelectedDealBrand] = useState<string | null>(null);
  const [expandedDealId, setExpandedDealId] = useState<number | null>(null);
  const [purchaseOrders, setPurchaseOrders] = useState<MaterialPurchaseOrderGroup[]>([]);
  const [purchaseOrdersLoading, setPurchaseOrdersLoading] = useState(false);
  const [showPurchaseOrdersModal, setShowPurchaseOrdersModal] = useState(false);
  const [downloadingMerchantId, setDownloadingMerchantId] = useState<number | null>(null);


  const [orderQuantityError, setOrderQuantityError] = useState<string>("");
  const [orderUnitPriceError, setOrderUnitPriceError] = useState<string>("");
  const [orderDateError, setOrderDateError] = useState<string>("");

  /* Delivery */
  const [showDeliveryModal, setShowDeliveryModal] = useState<boolean>(false);
  const [deliveryQuantity, setDeliveryQuantity] = useState<number | null>(null);
  const [deliveryDate, setDeliveryDate] = useState<Nullable<Date>>(null);
  const [selectedDeliveryItemId, setSelectedDeliveryItemId] = useState<number | null>(null);


  const [deliveryQuantityError, setDeliveryQuantityError] = useState<string>("");
  const [deliveryDateError, setDeliveryDateError] = useState<string>("");

  /* Onsite */
  const [showOnsiteModal, setShowOnsiteModal] = useState<boolean>(false);
  const [onsiteQuantity, setOnsiteQuantity] = useState<number | null>(null);
  const [onsiteDate, setOnsiteDate] = useState<Nullable<Date>>(null);
  const [selectedOnsiteItemId, setSelectedOnsiteItemId] = useState<number | null>(null);


  const [onsiteQuantityError, setOnsiteQuantityError] = useState<string>("");
  const [onsiteDateError, setOnsiteDateError] = useState<string>("");



  /*  */
  const [activeOrderItem, setActiveOrderItem] = useState<MaterialDataType | null>(null);
  const [activeDeliveredItem, setActiveDeliveredItem] = useState<MaterialDataType | null>(null);
  const [activeOnsiteItem, setActiveOnsiteItem] = useState<MaterialDataType | null>(null);

  const taxPercentage = Number(localStorage.getItem('material_tax_percentage'));

  // PERMISION HANDLER
  const useCanEdit = (screen: string, permission: string) => {
    return useSelector((state: RootState) =>
      hasPermission(state, screen, permission)
    );
  };

  const hasPermissionForAddMaterialItem = ((useCanEdit("Materials", "add_item") && projectDetails?.is_own_project) || projectDetails?.can_edit);
  const hasPermissionForEditMaterialItem = ((useCanEdit("Materials", "edit_item") && projectDetails?.is_own_project) || projectDetails?.can_edit);
  const hasPermissionForDeleteMaterialItem = ((useCanEdit("Materials", "delete_item") && projectDetails?.is_own_project) || projectDetails?.can_edit);

  // for the resons
  const dispatch = useAppDispatch();

  // for fetching data fro api tigger with state object actions
  useEffect(() => {
    dispatch(getAllMaterialProjectList());
    dispatch(getAllMaterialUnitList());
  }, []);

  // this is get data from state list and set projectlist
  const ProjectsList = useAppSelector((state) => state?.materialProject?.materialProjectList);
  const loadingPage = useAppSelector((state) => state?.materialProject?.loader);
  const error = useAppSelector((state) => state?.materialProject?.errorDisplay);
  const unitList = useAppSelector((state) => state?.materialUnit?.materialUnitList);

  //   chnage reail time responce with updated project info/
  useEffect(() => {
    if (location.state && location.state.materialList) {
      const globleproject = location.state.materialList as MaterialType;
      const listpro = ProjectsList.filter(
        (e) => e.project_id === globleproject.project_id
      );

      if (listpro?.length > 0) {
        setProjectDetails(listpro[0] as MaterialPojectType);
        setMaterialListData(location.state.materialList as MaterialType);
      }
    }
  }, [location.state, ProjectsList]);

  // change every project data changes.
  useEffect(() => {
    const globleMaterial = location.state.materialList as MaterialType;

    const materialList = projectDetails?.material_data?.filter(
      (e) => e.id === globleMaterial.id
    );

    if (materialList && materialList?.length > 0) {
      setMaterialListData(materialList![0] as MaterialType);
    }
  }, [projectDetails]);

  // set unit list ass well
  useEffect(() => {
    if (unitList && unitList?.length > 0) {
      setMaterialDataUnits(unitList);
    }
  }, [unitList]);




  //  TAX CALUCULATIONS
  useEffect(() => {
    if (isIncludeTax) {
      // Add tax to unit price
      const unitP = unitPrice + (unitPrice * taxPercentage / 100);
      setUnitPrice(unitP);

    } else {
      // Remove tax from unit price
      const unitP = unitPrice / (1 + taxPercentage / 100);
      setUnitPrice(unitP);

    }
  }, [isIncludeTax]);


  useEffect(() => {

    var subtotal = unitPrice * quantity;
    setSubTotal(subtotal);
    // add tax for unit price


  }, [unitPrice]);


  /* ***************************** Estimation ******************************* */
  const handleCloseMaterialItemModal = () => {
    setShowMaterialItemModal(false);
    handleClearMaterialItemFields();
    handleClearMaterialItemErrors();
  };

  const handleClearMaterialItemFields = () => {
    setItemName("");
    setQuantity(0);
    setMaterialUnit(null);
    setUnitPrice(0);
    setBaseUnitPrice(0);
    setIsIncludeTax(false);
    setSubTotal(0);
    setNote("");
  };

  const handleClearMaterialItemErrors = () => {
    setItemNameError("");
    setMaterialUnitError("");
    setQuantityError("");
    setUnitPriceError("");
    setSubTotalError("");
    setNoteError("");
  };

  const handleAddMaterialItem = () => {
    setDataState("Add");
    setShowMaterialItemModal(true);
  };

  // validation function as well.
  useEffect(() => {
    checkText(itemName!, false, setItemNameError);
  }, [itemName]);

  useEffect(() => {
    checkModelSelected(materialUnit != null, false, setMaterialUnitError);
  }, [materialUnit]);

  useEffect(() => {
    checkNumber(quantity!, false, setQuantityError);
  }, [quantity]);

  useEffect(() => {
    checkNumber(unitPrice!, false, setUnitPriceError);
  }, [unitPrice]);

  function checkCredentials(): boolean {
    const nameCheck = checkText(itemName!, true, setItemNameError);
    const unitCheck = checkModelSelected(
      materialUnit != null,
      true,
      setMaterialUnitError
    );
    const qtyCheck = checkNumber(quantity!, true, setQuantityError);
    const priceCheck = checkNumber(unitPrice!, true, setUnitPriceError);

    return nameCheck && unitCheck && qtyCheck && priceCheck;
  }

  // create materials item data
  const handleCreateMaterialItem = async (e: React.FormEvent) => {
    e.preventDefault();

    if (checkCredentials()) {
      setLoading(true);

      const data = await addMaterialItem(
        materialListData?.id!,
        materialUnit?.id!,
        itemName,
        quantity,
        unitPrice,
        subTotal,
        isIncludeTax ? 1 : 0,
        note
      );

      if (data.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialProjectList());

        setLoading(false);
        handleCloseMaterialItemModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Item created successfully.",
            life: 3000,
          });
        }
      } else {
        setLoading(false);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error creating the item.",
            life: 3000,
          });
        }
      }
    }
  };

  const handleEditMaterialItem = (data: MaterialEstimationType) => {
    if (data) {
      setSelectedItemData(data);
      setDataState("Edit");
      setShowMaterialItemModal(true);

      setItemName(data?.item_name || "");

      const selectedMaterialUnit = materialDataUnitsList?.find(
        (unit) => unit?.id === data?.unit_id
      );

      setMaterialUnit(selectedMaterialUnit || null);
      setQuantity(data?.quantity || 0);
      setIsIncludeTax(data?.include_tax === 1 ? true : false);

      const rawUnitPrice = data?.unit_price ?? 0;

      let basePrice = rawUnitPrice;

      if (data?.include_tax === 1 && taxPercentage > 0) {
        basePrice = rawUnitPrice / (1 + taxPercentage / 100);
      }

      setBaseUnitPrice(basePrice);
      setUnitPrice(rawUnitPrice);
      setSubTotal(data?.sub_total ?? 0);

      setNote(data?.note || "");
    } else {
      setSelectedItemData(null);
    }
  };

  // update materials item data
  const handleUpdateMaterialItem = async (e: React.FormEvent) => {
    e.preventDefault();

    if (checkCredentials()) {
      setLoading(true);

      const data = await updateMaterialItem(
        materialListData?.id!,
        materialUnit?.id!,
        itemName,
        quantity,
        unitPrice,
        subTotal,
        isIncludeTax ? 1 : 0,
        note,
        selectedItemData?.id!
      );

      if (data.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialProjectList());

        setLoading(false);
        handleCloseMaterialItemModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Item updated successfully.",
            life: 3000,
          });
        }
      } else {
        setLoading(false);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error updating the item.",
            life: 3000,
          });
        }
      }
    }
  };

  const handleDeleteMaterialItem = (data: any) => {
    const materialItemId = data?.id;

    confirmDialog({
      message: "Are you sure you want to delete this item?",
      header: "Confirm the deletion",
      icon: "bi bi-info-circle",
      defaultFocus: "accept",
      acceptClassName: "p-button-danger",
      accept: () => deleteMaterialItem(materialItemId!),
      dismissableMask: true,
      resizable: false,
      draggable: false,
    });
  };

  // delete materials item data
  const deleteMaterialItem = async (materialItemId: number) => {
    const result = await deleteMaterialItemData(materialItemId);

    if (result.statusCode === 200) {
      dispatch(getAllMaterialProjectList());

      if (toast.current) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Item deleted successfully.",
          life: 3000,
        });
      }
    } else {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Failed",
          detail: "There was an error deleting the item.",
          life: 3000,
        });
      }
    }
  };

  const materialItemModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">{dataState} item</h5>
      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseMaterialItemModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const materialItemModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={handleCloseMaterialItemModal}
      />

      <Button
        label={`${loading ? "Processing" : dataState === "Add" ? "Save" : "Update"}`}
        onClick={dataState === "Add" ? handleCreateMaterialItem : handleUpdateMaterialItem}
        loading={loading}
        className="custom_btn primary"
      />
    </div>
  );

  useEffect(() => {
    const base = Number(baseUnitPrice ?? 0);
    const qty = Number(quantity ?? 0);

    const taxAmount = isIncludeTax && taxPercentage !== 0
      ? (base * taxPercentage) / 100
      : 0;

    const finalUnit = round2(base + taxAmount);
    const newSubTotal = round2(qty * finalUnit);

    setUnitPrice(finalUnit);
    setSubTotal(newSubTotal);
  }, [baseUnitPrice, isIncludeTax, taxPercentage, quantity]);


  const handleQuantityChange = (e: InputNumberChangeEvent) => {
    const newQty = (e.value ?? 0) as number;
    setQuantity(newQty);
  };

  const handleUnitPriceChange = (e: InputNumberChangeEvent) => {
    const newBase = (e.value ?? 0) as number;
    setBaseUnitPrice(newBase);
    setIsIncludeTax(false);
  };

  const handleSubTotalChange = (e: InputNumberChangeEvent) => { };

  const handleCheckIncludeTax = (e: CheckboxChangeEvent) => {
    const checked = (e.checked ?? false) as boolean;
    setIsIncludeTax(checked);
  };

  const handleUnitChange = (e: any) => {
    setMaterialUnit(e.value as MaterialUnitType);
  };

  const handleChangeItemViewState = (state: "Estimation" | "Order" | "Delivered" | "Onsite") => {
    setItemViewState(state);
  };

  const estimationTableActionBody = (rowData: MaterialEstimationType) => {
    const data = rowData;
    return (
      <React.Fragment>
        <Tooltip
          key={`view_${data?.id}`}
          id={`view_${data?.id}`}
          target={`.view_${data?.id}`}
        />

        {hasPermissionForEditMaterialItem && (
          <Tooltip
            key={`edit_${data?.id}`}
            id={`edit_${data?.id}`}
            target={`.edit_${data?.id}`}
          />
        )}

        {hasPermissionForDeleteMaterialItem && (
          <Tooltip
            key={`delete_${data?.id}`}
            id={`delete_${data?.id}`}
            target={`.delete_${data?.id}`}
          />
        )}

        <div className="tabel_btn_area custom mt-1 mb-1">
          <button
            key={`viewNote`}
            className={`data_action_btn with_bg primary_table_btn view_${data?.id} p-ripple`}
            onClick={() => handleViewNote(data?.note!)}
            data-pr-tooltip={`View info`}
            data-pr-position="top"
          >
            <i className="bi bi-eye-fill"></i>
            <Ripple />
          </button>

          {hasPermissionForEditMaterialItem && (
            <button
              key={`editItem`}
              className={`data_action_btn with_bg primary_table_btn edit_${data?.id} p-ripple`}
              onClick={() => handleEditMaterialItem(data)}
              data-pr-tooltip={`Edit item`}
              data-pr-position="top"
            >
              <i className="bi bi-pencil-square"></i>
              <Ripple />
            </button>
          )}

          {hasPermissionForDeleteMaterialItem && (
            <button
              key={`deleteItem`}
              className={`data_action_btn with_bg danger_table_btn delete_${data?.id} p-ripple`}
              onClick={() => handleDeleteMaterialItem(data)}
              data-pr-tooltip={`Delete item`}
              data-pr-position="top"
            >
              <i className="bi bi-trash3"></i>
              <Ripple />
            </button>
          )}
        </div>
      </React.Fragment>
    );
  };

  const handleViewNote = (note: string) => {
    setShowNoteViewModal(true);

    if (note) {
      setSelectedItemNote(note);
    } else {
      setSelectedItemNote("");
    }
  };

  const handleCloseNoteViewModal = () => {
    setShowNoteViewModal(false);
    setSelectedItemNote("");
  };

  const noteViewModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">Information</h5>
      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseNoteViewModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const noteViewModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={handleCloseNoteViewModal}
      />
    </div>
  );
  /* *************************************************************************** */

  /* ********************************* Order *********************************** */
  const handleCloseOrderModal = () => {
    setShowOrderModal(false);
    handleClearOrderFields();
    handleClearOrderErrors();
  };

  const handleClearOrderFields = () => {
    setOrderQuantity(null);
    setOrderUnitPrice(null);
    setOrderDate(null);
    setOrderUnitLastPrice(null);
    setSelectedOrderItemId(null);
    setSelectedDeal(null);
    setMaterialDeals([]);
    setDealSearch("");
    setSelectedDealShop(null);
    setSelectedDealBrand(null);
    setExpandedDealId(null);
    setShowBestDealModal(false);
  };

  const handleClearOrderErrors = () => {
    setOrderQuantityError("");
    setOrderUnitPriceError("");
    setOrderDateError("");
  };

  //  order validations
  useEffect(() => {
    checkNumber(orderQuantity!, false, setOrderQuantityError);
  }, [orderQuantity]);

  useEffect(() => {
    checkNumber(orderUnitPrice!, false, setOrderUnitPriceError);
  }, [orderUnitPrice]);

  useEffect(() => {
    checkText(formatDate(orderDate!), false, setOrderDateError)
  }, [orderDate]);

  function orderConditionCheck(): Boolean {
    const check1 = checkNumber(orderQuantity!, true, setOrderQuantityError);
    const check2 = checkNumber(orderUnitPrice!, true, setOrderUnitPriceError);
    const check3 = checkText(formatDate(orderDate!), true, setOrderDateError);

    return check1 && check2 && check3;
  }
  // 

  const handleAddOrderItem = (orderItem: MaterialDataType) => {
    if (orderItem) {
      setDataState('Add');
      setActiveOrderItem(orderItem);
      setOrderUnitPrice(orderItem?.last_buying_price || null)
      setOrderUnitLastPrice(orderItem?.last_buying_price || 0);
      setSelectedDeal(null);
      setShowOrderModal(true);
    } else {
      setActiveOrderItem(null);
      setOrderUnitLastPrice(null);
    }
  }

  const handleFindBestDeal = async () => {
    const materialName = activeOrderItem?.item_name?.trim();
    if (!materialName) return;

    setShowBestDealModal(true);
    setDealLoading(true);
    setDealSearch("");
    setSelectedDealShop(null);
    setSelectedDealBrand(null);
    try {
      setMaterialDeals(await getMaterialResultData(materialName));
    } catch (error) {
      setMaterialDeals([]);
      toast.current?.show({
        severity: "error",
        summary: "Unable to find deals",
        detail: "Please try again later.",
        life: 3000,
      });
    } finally {
      setDealLoading(false);
    }
  };

  const handleSelectDeal = (deal: MaterialDealType) => {
    setSelectedDeal(deal);
    const dealPrice = Number(deal.trade_price_per_unit || deal.standard_price_per_unit);
    setOrderUnitPrice(Number.isFinite(dealPrice) ? dealPrice : null);
    setShowBestDealModal(false);
  };

  const dealShopOptions = Array.from(new Set(materialDeals.map((deal) => deal.merchant?.merchant).filter(Boolean)));
  const dealBrandOptions = Array.from(new Set(materialDeals.map((deal) => deal.manufacturer?.manufacturer).filter(Boolean)));
  const filteredMaterialDeals = materialDeals.filter((deal) => {
    const searchValue = dealSearch.trim().toLowerCase();
    const searchable = [deal.material?.material_name, deal.merchant?.merchant, deal.manufacturer?.manufacturer, deal.measurement?.measurement]
      .filter(Boolean).join(" ").toLowerCase();
    return (!searchValue || searchable.includes(searchValue))
      && (!selectedDealShop || deal.merchant?.merchant === selectedDealShop)
      && (!selectedDealBrand || deal.manufacturer?.manufacturer === selectedDealBrand);
  });

  const loadPurchaseOrders = useCallback(async (showError = true) => {
    const materialDataId = materialListData?.id;

    if (!materialDataId) {
      setPurchaseOrders([]);
      return [];
    }

    setPurchaseOrdersLoading(true);

    try {
      const list = await getMaterialPurchaseOrderList(materialDataId);
      setPurchaseOrders(list);

      if (list.length === 0) {
        setShowPurchaseOrdersModal(false);
      }

      return list;
    } catch (error) {
      setPurchaseOrders([]);

      if (showError) {
        toast.current?.show({
          severity: "error",
          summary: "Unable to load purchase orders",
          detail: "Please try again later.",
          life: 3000,
        });
      }

      return [];
    } finally {
      setPurchaseOrdersLoading(false);
    }
  }, [materialListData?.id]);

  useEffect(() => {
    if (itemViewState === "Order") {
      loadPurchaseOrders();
    }
  }, [itemViewState, loadPurchaseOrders]);

  const promptPurchaseOrderShare = (
    blob: Blob | null,
    fileName: string,
    merchantName: string,
    downloadUrl: string | null
  ) => {
    const file = blob
      ? new File([blob], fileName, {
          type: blob.type || "application/pdf",
        })
      : null;

    confirmDialog({
      header: "Share purchase order",
      message: `The purchase order for ${merchantName} has been downloaded. Would you like to share it now?`,
      icon: "bi bi-share",
      acceptLabel: "Share",
      rejectLabel: "Not now",
      defaultFocus: "accept",
      dismissableMask: true,
      accept: async () => {
        try {
          const nav = navigator as any;
          const shareData = {
            title: `Purchase order - ${merchantName}`,
            text: `Purchase order for ${merchantName}`,
            ...(file ? { files: [file] } : {}),
            ...(!file && downloadUrl ? { url: downloadUrl } : {}),
          };

          if (
            typeof nav.share !== "function" ||
            (file &&
              typeof nav.canShare === "function" &&
              !nav.canShare({ files: [file] }))
          ) {
            toast.current?.show({
              severity: "info",
              summary: "File downloaded",
              detail: "Local sharing is not supported by this browser. You can share the downloaded file manually.",
              life: 4000,
            });
            return;
          }

          await nav.share(shareData);
        } catch (error: any) {
          if (error?.name !== "AbortError") {
            toast.current?.show({
              severity: "error",
              summary: "Unable to share",
              detail: "The file was downloaded, but local sharing could not be opened.",
              life: 3000,
            });
          }
        }
      },
    });
  };

  const openPurchaseOrderDownloadUrl = (
    downloadUrl: string,
    fileName: string
  ) => {
    const downloadLink = document.createElement("a");
    downloadLink.href = downloadUrl;
    downloadLink.download = fileName;
    downloadLink.target = "_blank";
    downloadLink.rel = "noopener noreferrer";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
  };

  const handleDownloadPurchaseOrder = async (
    purchaseOrder: MaterialPurchaseOrderGroup
  ) => {
    const materialDataId = materialListData?.id;
    const merchantName = purchaseOrder.merchant?.merchant || "Merchant";

    if (!materialDataId) return;

    setDownloadingMerchantId(purchaseOrder.merchant_id);

    try {
      const download = await downloadMaterialPurchaseOrder(
        materialDataId,
        purchaseOrder.merchant_id,
        merchantName
      );

      if (download.blob) {
        saveAs(download.blob, download.fileName);
      } else if (download.downloadUrl) {
        openPurchaseOrderDownloadUrl(
          download.downloadUrl,
          download.fileName
        );
      } else {
        throw new Error("Purchase order file was not returned");
      }

      setShowPurchaseOrdersModal(false);
      promptPurchaseOrderShare(
        download.blob,
        download.fileName,
        merchantName,
        download.downloadUrl
      );

      // Close the current PO view, then recheck only the PO list in the
      // background. This updates the header button without a full-page refresh.
      await loadPurchaseOrders(false);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Download failed",
        detail:
          error?.message ||
          "Unable to download this purchase order. Please try again.",
        life: 5000,
      });
    } finally {
      setDownloadingMerchantId(null);
    }
  };

  const getPurchaseOrderTotal = (purchaseOrder: MaterialPurchaseOrderGroup) => {
    return purchaseOrder.material_list.reduce((total, material) => {
      const quantity = Number(material.quantity) || 0;
      const unitPrice = Number(
        material.material_bank_data?.trade_price_per_unit ??
        material.material_bank_data?.standard_price_per_unit ??
        0
      );

      return total + quantity * unitPrice;
    }, 0);
  };

  // database function create material orders
  const handleCreateOrderItem = async (e: React.FormEvent) => {
    e.preventDefault();

    if (orderConditionCheck()) {
      setLoading(true);

      const data = await addOrder(
        activeOrderItem?.id!,
        orderQuantity!,
        orderUnitPrice!,
        formatDate(orderDate!),
        selectedDeal,
      );

      if (data.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialProjectList());
        await loadPurchaseOrders(false);
        setLoading(false);
        handleCloseOrderModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Order created successfully.",
            life: 3000,
          });
        }
      } else {
        setLoading(false);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error creating the order.",
            life: 3000,
          });
        }
      }
    }
  };

  const handleEditOrderItem = (data: MaterialStructureType) => {
    if (data) {
      setOrderQuantity(data?.order?.quantity || 0);
      setOrderDate(data?.date ? parseStringToDate(data?.date) : null);
      setOrderUnitPrice(data?.order?.unit_price || 0)
      setOrderUnitLastPrice(activeOrderItem?.last_buying_price || 0);
      setSelectedOrderItemId(data?.order?.id || 0)

      setDataState('Edit');
      setShowOrderModal(true);
    } else {
      setOrderQuantity(null);
      setOrderDate(null);
      setOrderUnitLastPrice(null);
    }
  };

  // handle order update functions
  const handleUpdateOrderItem = async (e: React.FormEvent) => {
    e.preventDefault();

    if (orderConditionCheck()) {
      setLoading(true);

      const data = await updateOrder(
        orderQuantity!,
        orderUnitPrice!,
        formatDate(orderDate!),
        selectedOrderItemId!,
      );

      if (data.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialProjectList());
        await loadPurchaseOrders(false);

        setLoading(false);
        handleCloseOrderModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Order udated successfully.",
            life: 3000,
          });
        }
      } else {
        setLoading(false);
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error creating the order.",
            life: 3000,
          });
        }
      }
    }
  };

  const handleDeleteOrderItem = (data: MaterialStructureType) => {
    const orderItemId = data?.order?.id;

    confirmDialog({
      message: "Are you sure you want to delete this material order record? This action cannot be undone.",
      header: "Confirm the deletion",
      icon: "bi bi-info-circle",
      defaultFocus: "accept",
      acceptClassName: "p-button-danger",
      accept: () => deleteOrderItem(orderItemId!),
      dismissableMask: true,
      resizable: false,
      draggable: false,
    });
  };

  // delete order record
  const deleteOrderItem = async (orderItemId: number) => {
    const result = await deleteOrder(orderItemId);

    if (result.statusCode === 200) {
      dispatch(getAllMaterialProjectList());
      await loadPurchaseOrders(false);
      if (toast.current) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Order Record deleted successfully.",
          life: 3000,
        });
      }
    } else {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Failed",
          detail: "There was an error deleting the order record.",
          life: 3000,
        });
      }
    }
  };

  const orderModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">
        Order unit

        {materialListData && (
          <React.Fragment>
            <br />
            <small className="modal_sub_title text-capitalize">
              ({materialListData?.material_category?.category_name})
            </small>
          </React.Fragment>
        )}
      </h5>

      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseOrderModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const orderModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Cancel"
        className="custom_btn secondary"
        onClick={handleCloseOrderModal}
      />

      <Button
        label={`${loading ? "Processing" : dataState === 'Add' ? "Save" : 'Update'}`}
        onClick={dataState === 'Add' ? handleCreateOrderItem : handleUpdateOrderItem}
        loading={loading}
        className="custom_btn primary"
      />
    </div>
  );

  const handleOrderQuantityChange = (e: InputNumberChangeEvent) => {
    if (e.value != null) {
      setOrderQuantity(e.value);
    } else {
      setOrderQuantity(null);
    }
  };

  const handleOrderUnitPriceChange = (e: InputNumberChangeEvent) => {
    if (e.value != null) {
      setOrderUnitPrice(e.value);
    } else {
      setOrderUnitPrice(null);
    }
  };

  const orderItemActionBody = (orderItem: MaterialDataType) => {
    return (
      <div className="table_action_btn_area justify-content-end pe-sm-3 mt-1 mb-1">
        <button
          className={`table_action_btn white p-ripple`}
          onClick={(e) => {
            handleViewItemInfo(orderItem?.note || '');
            e.stopPropagation();
          }}>
          Info
          <Ripple />
        </button>

        {hasPermissionForAddMaterialItem && (
          <button
            className={`table_action_btn white p-ripple`}
            onClick={(e) => {
              handleAddOrderItem(orderItem);
              e.stopPropagation();
            }}>
            Add
            <Ripple />
          </button>
        )}
      </div>
    )
  }

  const orderSubItemActionBody = (order: MaterialStructureType) => {
    return (
      <div className="table_action_btn_area sub justify-content-end pe-sm-3">
        <React.Fragment>
          {hasPermissionForEditMaterialItem && (
            <button
              className={`data_action_btn sm with_bg primary_table_btn p-ripple`}
              onClick={() => handleEditOrderItem(order)}
            >
              <i className="bi bi-pencil-square"></i>
              <Ripple />
            </button>
          )}

          {hasPermissionForDeleteMaterialItem && (
            <button
              className={`data_action_btn sm with_bg danger_table_btn p-ripple`}
              onClick={() => handleDeleteOrderItem(order)}
            >
              <i className="bi bi-trash3"></i>
              <Ripple />
            </button>
          )}
        </React.Fragment>
      </div>
    )
  }
  /* *************************************************************************** */


  /* ********************************************************************* Delivery */
  const handleCloseDeliveryModal = () => {
    setShowDeliveryModal(false);
    handleResetDeliveryFields();
    handleResetDeliveryErrors();
  }

  const handleResetDeliveryFields = () => {
    setDeliveryDate(null);
    setDeliveryQuantity(null);
    setSelectedDeliveryItemId(null);
  }

  const handleResetDeliveryErrors = () => {
    setDeliveryDateError('');
    setDeliveryQuantityError('');
  }

  const handleAddDeliveryUnit = (deliveredItem: MaterialDataType) => {
    if (deliveredItem) {
      setActiveDeliveredItem(deliveredItem);
      setShowDeliveryModal(true);
      setDataState('Add');
    } else {
      setActiveDeliveredItem(null);
    }
  }

  //  Delevery validations
  useEffect(() => {
    checkNumber(deliveryQuantity!, false, setDeliveryQuantityError);
  }, [deliveryQuantity]);

  useEffect(() => {
    checkText(formatDate(deliveryDate!), false, setDeliveryDateError);
  }, [deliveryDate]);

  function deleveryConditionCheck(): Boolean {
    const check1 = checkText(formatDate(deliveryDate!), true, setDeliveryDateError);
    const check2 = checkNumber(deliveryQuantity!, true, setDeliveryQuantityError);

    return check1 && check2;
  }
  // 

  // Create delivery record
  const handleCreateDeliveryUnit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (deleveryConditionCheck()) {
      setLoading(true);

      const data = await addDeleverd(
        activeDeliveredItem?.id!,
        deliveryQuantity!,
        formatDate(deliveryDate!)
      );

      if (data.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialProjectList());
        setLoading(false);
        handleCloseDeliveryModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Delivery record created successfully.",
            life: 3000,
          });
        }
      } else {
        setLoading(false);
        handleCloseDeliveryModal();
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error creating the delivery record.",
            life: 3000,
          });
        }
      }
    }
  }

  const handleEditDeliveryUnit = (data: MaterialStructureType) => {
    if (data) {
      setDeliveryQuantity(data?.delivered?.delivered_quantity || 0);
      setDeliveryDate(data?.date ? parseStringToDate(data?.date) : null);
      setSelectedDeliveryItemId(data?.delivered?.id || 0);
      setDataState('Edit');
      setShowDeliveryModal(true);
    } else {
      setDeliveryQuantity(null);
      setDeliveryDate(null);
    }
  }

  // update delivery record
  const handleUpdateDeliveryUnit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (deleveryConditionCheck()) {
      setLoading(true);

      const data = await updateDeleverd(
        deliveryQuantity!,
        formatDate(deliveryDate!),
        selectedDeliveryItemId!
      );

      if (data.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialProjectList());

        setLoading(false);
        handleCloseDeliveryModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Delivery record updated successfully.",
            life: 3000,
          });
        }
      } else {
        setLoading(false);
        handleCloseDeliveryModal();

        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error updating the delivery record.",
            life: 3000,
          });
        }
      }
    }
  }

  const handleDeleteDeliveryUnit = (data: MaterialStructureType) => {
    const deliveryUnitId = data?.delivered?.id;

    confirmDialog({
      message: "Are you sure you want to delete this material delivery record? This action cannot be undone.",
      header: "Confirm the deletion",
      icon: "bi bi-info-circle",
      defaultFocus: "accept",
      acceptClassName: "p-button-danger",
      accept: () => deleteDeliveryUnit(deliveryUnitId!),
      dismissableMask: true,
      resizable: false,
      draggable: false,
    });
  };

  // delete delivery record
  const deleteDeliveryUnit = async (deliveryUnitId: number) => {
    const result = await deleteDeleverd(deliveryUnitId);

    if (result.statusCode === 200) {
      dispatch(getAllMaterialProjectList());

      if (toast.current) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Delivery Record deleted successfully.",
          life: 3000,
        });
      }
    } else {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Failed",
          detail: "There was an error deleting the delivery record.",
          life: 3000,
        });
      }
    }
  };

  const deliveryModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">
        {dataState} Delivered unit

        {materialListData && (
          <React.Fragment>
            <br />
            <small className="modal_sub_title text-capitalize">
              ({materialListData?.material_category?.category_name})
            </small>
          </React.Fragment>
        )}
      </h5>

      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseDeliveryModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const deliveryModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Cancel"
        className="custom_btn secondary"
        onClick={handleCloseDeliveryModal}
      />

      <Button
        label={`${loading ? "Processing" : dataState === 'Add' ? "Save" : 'Update'}`}
        onClick={dataState === 'Add' ? handleCreateDeliveryUnit : handleUpdateDeliveryUnit}
        loading={loading}
        className="custom_btn primary"
      />
    </div>
  );

  const handleDeliveryQuantityChange = (e: InputNumberChangeEvent) => {
    if (e.value != null) {
      setDeliveryQuantity(e.value);
    } else {
      setDeliveryQuantity(null);
    }
  };

  const deliveredItemActionBody = (deliveredItem: MaterialDataType) => {
    return (
      <div className="table_action_btn_area justify-content-end pe-sm-3 mt-1 mb-1">
        <button
          className={`table_action_btn white p-ripple`}
          onClick={(e) => {
            handleViewItemInfo(deliveredItem?.note || '');
            e.stopPropagation();
          }}>
          Info
          <Ripple />
        </button>

        {hasPermissionForAddMaterialItem && (
          <button
            className={`table_action_btn white p-ripple`}
            onClick={(e) => {
              handleAddDeliveryUnit(deliveredItem);
              e.stopPropagation();
            }}>
            Add
            <Ripple />
          </button>
        )}
      </div>
    )
  }

  const deliveredSubItemActionBody = (delivered: MaterialStructureType) => {
    return (
      <div className="table_action_btn_area sub justify-content-end pe-sm-3">
        <React.Fragment>
          {hasPermissionForEditMaterialItem && (<button
            className={`data_action_btn sm with_bg primary_table_btn p-ripple`}
            onClick={() => handleEditDeliveryUnit(delivered)}
          >
            <i className="bi bi-pencil-square"></i>
            <Ripple />
          </button>)}

          {hasPermissionForDeleteMaterialItem && (<button
            className={`data_action_btn sm with_bg danger_table_btn p-ripple`}
            onClick={() => handleDeleteDeliveryUnit(delivered)}
          >
            <i className="bi bi-trash3"></i>
            <Ripple />
          </button>)}
        </React.Fragment>
      </div>
    )
  }
  /* ********************************************************************* */


  /* ********************************************************************* Onsite */
  const handleCloseOnsiteModal = () => {
    setShowOnsiteModal(false);
    handleResetOnsiteFields();
    handleResetOnsiteErrors();
  }

  const handleResetOnsiteFields = () => {
    setOnsiteDate(null);
    setOnsiteQuantity(null);
    setSelectedOnsiteItemId(null);
  }

  const handleResetOnsiteErrors = () => {
    setOnsiteDateError('');
    setOnsiteQuantityError('');
  }

  const handleAddOnsiteUnit = (onsiteItem: MaterialDataType) => {
    if (onsiteItem) {
      setActiveOnsiteItem(onsiteItem);
      setShowOnsiteModal(true);
      setDataState('Add');
    } else {
      setActiveOnsiteItem(null);
    }
  }

  // workking with onsite data
  //  Onsite validations
  useEffect(() => {
    checkNumber(onsiteQuantity!, false, setOnsiteQuantityError);
  }, [onsiteQuantity]);

  useEffect(() => {
    checkText(formatDate(onsiteDate!), false, setOnsiteDateError);
  }, [onsiteDate]);

  function onsiteConditionCheck(): Boolean {
    const check1 = checkText(formatDate(onsiteDate!), true, setOnsiteDateError);
    const check2 = checkNumber(onsiteQuantity!, true, setOnsiteQuantityError);
    return check1 && check2;
  }
  // 

  // Create onsite record
  const handleCreateOnsiteUnit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (onsiteConditionCheck()) {
      setLoading(true);

      const data = await addOnsite(
        activeOnsiteItem?.id!,
        onsiteQuantity!,
        formatDate(onsiteDate!)
      );

      if (data.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialProjectList());

        setLoading(false);
        handleCloseOnsiteModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Onsite record created successfully.",
            life: 3000,
          });
        }
      } else {
        setLoading(false);
        handleCloseOnsiteModal();

        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error creating the onsite record.",
            life: 3000,
          });
        }
      }
    }
  }

  const handleEditOnsiteUnit = (data: MaterialStructureType) => {
    if (data) {
      setOnsiteQuantity(data?.onsite?.onsite_quantity || 0);
      setOnsiteDate(data?.date ? parseStringToDate(data?.date) : null);
      setSelectedOnsiteItemId(data?.onsite?.id || 0);
      setDataState('Edit');
      setShowOnsiteModal(true);
    } else {
      setOnsiteQuantity(null);
      setOnsiteDate(null);
    }
  }

  // update onsite record
  const handleUpdateOnsiteUnit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (onsiteConditionCheck()) {
      setLoading(true);

      const data = await updateOnsite(
        onsiteQuantity!,
        formatDate(onsiteDate!),
        selectedOnsiteItemId!
      );

      if (data.statusCode === 200) {
        // call dispach event
        dispatch(getAllMaterialProjectList());

        setLoading(false);
        handleCloseOnsiteModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Onsite record updated successfully.",
            life: 3000,
          });
        }
      } else {
        setLoading(false);
        handleCloseOnsiteModal();

        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error updating the onsite record.",
            life: 3000,
          });
        }
      }
    }
  }

  const handleDeleteOnsiteUnit = (data: MaterialStructureType) => {
    const onsiteUnitId = data?.onsite?.id;

    confirmDialog({
      message: "Are you sure you want to delete this material onsite record? This action cannot be undone.",
      header: "Confirm the deletion",
      icon: "bi bi-info-circle",
      defaultFocus: "accept",
      acceptClassName: "p-button-danger",
      accept: () => deleteOnsiteUnit(onsiteUnitId!),
      dismissableMask: true,
      resizable: false,
      draggable: false,
    });
  };

  // delete onsite record
  const deleteOnsiteUnit = async (onsiteUnitId: number) => {
    const result = await deleteOnsite(onsiteUnitId);

    if (result.statusCode === 200) {
      dispatch(getAllMaterialProjectList());
      if (toast.current) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Onsite record deleted successfully.",
          life: 3000,
        });
      }
    } else {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Failed",
          detail: "There was an error deleting the onsite record.",
          life: 3000,
        });
      }
    }
  };

  const onsiteModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">
        Onsite unit

        {materialListData && (
          <React.Fragment>
            <br />
            <small className="modal_sub_title text-capitalize">
              ({materialListData?.material_category?.category_name})
            </small>
          </React.Fragment>
        )}
      </h5>

      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseOnsiteModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const onsiteModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Cancel"
        className="custom_btn secondary"
        onClick={handleCloseOnsiteModal}
      />

      <Button
        label={`${loading ? "Processing" : dataState === 'Add' ? "Save" : 'Update'}`}
        onClick={dataState === 'Add' ? handleCreateOnsiteUnit : handleUpdateOnsiteUnit}
        loading={loading}
        className="custom_btn primary"
      />
    </div>
  );

  const handleOnsiteQuantityChange = (e: InputNumberChangeEvent) => {
    if (e.value != null) {
      setOnsiteQuantity(e.value);
    } else {
      setOnsiteQuantity(null);
    }
  };

  const onsiteItemActionBody = (onsiteItem: MaterialDataType) => {
    return (
      <div className="table_action_btn_area justify-content-end pe-sm-3 mt-1 mb-1">
        <button
          className={`table_action_btn white p-ripple`}
          onClick={(e) => {
            handleViewItemInfo(onsiteItem?.note || '');
            e.stopPropagation();
          }}>
          Info
          <Ripple />
        </button>

        {hasPermissionForAddMaterialItem && (
          <button
            className={`table_action_btn white p-ripple`}
            onClick={(e) => {
              handleAddOnsiteUnit(onsiteItem);
              e.stopPropagation();
            }}>
            Add
            <Ripple />
          </button>
        )}
      </div>
    )
  }

  const onsiteSubItemActionBody = (onsite: MaterialStructureType) => {
    return (
      <div className="table_action_btn_area sub justify-content-end pe-sm-3">
        <React.Fragment>
          {hasPermissionForEditMaterialItem && (<button
            className={`data_action_btn sm with_bg primary_table_btn p-ripple`}
            onClick={() => handleEditOnsiteUnit(onsite)}
          >
            <i className="bi bi-pencil-square"></i>
            <Ripple />
          </button>)}

          {hasPermissionForDeleteMaterialItem && (<button
            className={`data_action_btn sm with_bg danger_table_btn p-ripple`}
            onClick={() => handleDeleteOnsiteUnit(onsite)}
          >
            <i className="bi bi-trash3"></i>
            <Ripple />
          </button>)}
        </React.Fragment>
      </div>
    )
  }
  /* ********************************************************************* */


  /*  */
  // useEffect(() => {
  //   const activeItem = materialListData?.data?.[0];
  //   if (!activeItem) return;

  //   switch (itemViewState) {
  //     case "Order":
  //       setActiveOrderItem(activeItem);
  //       break;
  //     case "Delivered":
  //       setActiveDeliveredItem(activeItem);
  //       break;
  //     case "Onsite":
  //       setActiveOnsiteItem(activeItem);
  //       break;
  //     default:
  //       break;
  //   }
  // }, [itemViewState, materialListData]);


  useEffect(() => {
    const list = materialListData?.data ?? [];
    if (!list.length) return;

    let activeItem: MaterialDataType | undefined;

    switch (itemViewState) {
      case "Order":
        activeItem = list.find((item) => item.id === activeOrderItem?.id);
        if (!activeItem) activeItem = list[0];
        setActiveOrderItem(activeItem);
        break;

      case "Delivered":
        activeItem = list.find((item) => item.id === activeDeliveredItem?.id);
        if (!activeItem) activeItem = list[0];
        setActiveDeliveredItem(activeItem);
        break;

      case "Onsite":
        activeItem = list.find((item) => item.id === activeOnsiteItem?.id);
        if (!activeItem) activeItem = list[0];
        setActiveOnsiteItem(activeItem);
        break;

      default:
        break;
    }
  }, [itemViewState, materialListData]);

  useEffect(() => {
    if (activeOrderItem && activeOrderItem !== null) {
      setActiveOrderItem(activeOrderItem)
    }
  }, [activeOrderItem])

  const countLabelBody = (count: number) => {
    return (
      <div className="item_count_lable custom text-end">
        <span>
          {(count ?? 0)?.toLocaleString("en-GB", {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}
        </span>
      </div>
    )
  }

  const handleToggleItem = (materialItem: MaterialDataType) => {
    const toggle = (prevSelected: MaterialDataType | null) =>
      prevSelected?.id === materialItem.id ? null : materialItem;

    switch (itemViewState) {
      case "Order":
        setActiveOrderItem(toggle);
        break;
      case "Delivered":
        setActiveDeliveredItem(toggle);
        break;
      case "Onsite":
        setActiveOnsiteItem(toggle);
        break;
      default:
        break;
    }

    goToTop();
  };

  const handleViewItemInfo = (materialItemNote: string) => {
    setShowNoteViewModal(true);

    if (materialItemNote) {
      setSelectedItemNote(materialItemNote);
    } else {
      setSelectedItemNote("");
    }
  }
  /*  */


  /* **************************** Common functions ***************************** */
  const goBack = () => {
    navigate(-1);
    goToTop();
  };
  /* *************************************************************************** */

  return (
    <React.Fragment>
      <Toast ref={toast} />

      <div className="main_content layout_padding show_side_bar">
        {loadingPage ? (
          <PageLoader />
        ) : error ? (
          <ErrorPage errorMessage={error} />
        ) : (
          <div className="page_content mt-0">
            <div className="page_breadcrumb_area">
              <ul className="page_breadcrumb">
                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                  Materials
                </li>

                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                  {materialListData?.material_category?.category_name} - {materialListData?.material_sub_category?.sub_category_name}
                </li>

                <li className="page_breadcrumb_item active">
                  Items
                </li>
              </ul>

              <button className="back_btn p-ripple" onClick={goBack}>
                <i className="bi bi-arrow-left me-2"></i>
                Back
                <Ripple />
              </button>
            </div>

            <div className="page_header_area mt-sm-3 mt-3">
              <div className="contact_tabs">
                <div className="contact_tab_item">
                  <button
                    type="button"
                    role="tab"
                    className={`contact_tab_btn fixed_width ${itemViewState === "Estimation" ? "active" : ""} p-ripple`}
                    onClick={() => handleChangeItemViewState("Estimation")}
                  >
                    Estimation
                    <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                  </button>
                </div>

                <div className="contact_tab_item">
                  <button
                    type="button"
                    role="tab"
                    className={`contact_tab_btn fixed_width ${itemViewState === "Order" ? "active" : ""} p-ripple`}
                    onClick={() => handleChangeItemViewState("Order")}
                  >
                    Order
                    <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                  </button>
                </div>

                <div className="contact_tab_item">
                  <button
                    type="button"
                    role="tab"
                    className={`contact_tab_btn fixed_width ${itemViewState === "Delivered" ? "active" : ""} p-ripple`}
                    onClick={() => handleChangeItemViewState("Delivered")}
                  >
                    Delivered
                    <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                  </button>
                </div>

                <div className="contact_tab_item">
                  <button
                    type="button"
                    role="tab"
                    className={`contact_tab_btn fixed_width ${itemViewState === "Onsite" ? "active" : ""} p-ripple`}
                    onClick={() => handleChangeItemViewState("Onsite")}
                  >
                    Onsite
                    <Ripple />
                  </button>
                </div>
              </div>

              {(
                (itemViewState === "Estimation" && hasPermissionForAddMaterialItem) ||
                (itemViewState === "Order" && purchaseOrders.length > 0)
              ) && (
                <div className="page_action_area">
                  {itemViewState === "Estimation" && hasPermissionForAddMaterialItem && (
                    <button
                      className="add_data_link_btn p-ripple"
                      onClick={handleAddMaterialItem}
                    >
                      <i className="bi bi-plus-circle"></i>
                      <span>Add Item</span>
                      <Ripple />
                    </button>
                  )}

                  {itemViewState === "Order" && purchaseOrders.length > 0 && (
                    <button
                      type="button"
                      className="add_data_link_btn material_purchase_order_btn p-ripple"
                      onClick={() => setShowPurchaseOrdersModal(true)}
                      disabled={purchaseOrdersLoading}
                    >
                      <i className={`bi ${purchaseOrdersLoading ? "bi-arrow-repeat purchase_order_spin" : "bi-file-earmark-text"}`}></i>
                      <span>Purchase Order(s)</span>
                      <small>{purchaseOrders.length}</small>
                      <Ripple />
                    </button>
                  )}
                </div>
              )}
            </div>

            {materialListData && materialListData !== null && (
              <React.Fragment>
                {itemViewState === "Estimation" && (
                  <div className="page_main_content_section p-0">
                    {materialListData?.estimation && materialListData?.estimation?.length > 0 ? (
                      <DataTable
                        value={materialListData?.estimation as MaterialEstimationType[]}
                        paginator
                        size="small"
                        rows={10}
                        emptyMessage="No item(s) found!"
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        tableStyle={{ minWidth: "50rem" }}
                        paginatorDropdownAppendTo={"self"}
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        currentPageReportTemplate="{first} to {last} of {totalRecords}"
                        className="page_table p-0 p-sm-2"
                        rowHover
                        scrollable
                      >
                        <Column
                          header="Item name"
                          headerClassName="text_purple"
                          field="item_name"
                          body={(rowData: MaterialEstimationType) => (
                            <span className="text-capitalize text_purple text_bold">
                              {rowData?.item_name ? rowData?.item_name : "---"}
                            </span>
                          )}
                          style={{ width: "30%" }}
                        ></Column>

                        <Column
                          header="Quantity"
                          headerClassName="text_purple"
                          alignHeader={"center"}
                          field="quantity"
                          body={(rowData: MaterialEstimationType) => (
                            <span className="text-capitalize text_no_wrap flex_center text-center">
                              {rowData?.quantity !== null
                                ? String(rowData?.quantity).padStart(2, "0")
                                : "00"}
                            </span>
                          )}
                          style={{ width: "10%" }}
                        ></Column>

                        <Column
                          header="Unit price"
                          headerClassName="text_purple"
                          alignHeader={"right"}
                          field="unit_price"
                          body={(rowData: MaterialEstimationType) => (
                            <span className="text-capitalize text_no_wrap flex_end text-end">
                              {currencySymbol}&nbsp;
                              {rowData?.unit_price !== null
                                ? rowData?.unit_price?.toLocaleString("en-GB", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })
                                : "0.00"}
                            </span>
                          )}
                          style={{ width: "10%" }}
                        ></Column>

                        <Column
                          header="Included tax"
                          headerClassName="text_purple"
                          alignHeader={"center"}
                          field="include_tax"
                          body={(rowData: MaterialEstimationType) => (
                            <span className="text-capitalize text_no_wrap flex_center text-end">
                              {rowData?.include_tax === 1 && rowData?.percentage !== null
                                ? rowData?.percentage + "%"
                                : "---"}
                            </span>
                          )}
                          style={{ width: "10%" }}
                        ></Column>

                        <Column
                          header="Subtotal"
                          headerClassName="text_purple"
                          alignHeader={"right"}
                          field="sub_total"
                          body={(rowData: MaterialEstimationType) => (
                            <span className="text-capitalize text_no_wrap flex_end text_bold text-end">
                              {currencySymbol}&nbsp;
                              {rowData?.sub_total !== null
                                ? rowData?.sub_total?.toLocaleString("en-GB", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })
                                : "0.00"}
                            </span>
                          )}
                          style={{ width: "20%" }}
                        ></Column>

                        <Column
                          alignHeader="center"
                          body={estimationTableActionBody}
                          frozen={window.innerWidth > 575}
                          alignFrozen="right"
                        ></Column>
                      </DataTable>
                    ) : (
                      <NoDataCreated message={`No item(s) found.`} />
                    )}
                  </div>
                )}

                {itemViewState === "Order" && (
                  <div className="page_main_content_section p-sm-2 p-0">
                    {materialListData?.data && materialListData?.data?.length > 0 ? (
                      <div className="pck_track_list_area">
                        <div className="table-responsive pck_track_list_table_area">
                          <table className="table pck_track_list_table">
                            <thead>
                              <tr>
                                <th style={{ width: "4%" }} className="text_no_wrap toggle_column"></th>

                                <th style={{ width: "20%" }} className="text_no_wrap item_title_column">
                                  Item
                                </th>

                                <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                  Estimation
                                </th>

                                <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                  Order
                                </th>

                                <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                  Delivered
                                </th>

                                <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                  Onsite
                                </th>

                                <th style={{ width: "16%" }} className="text-center item_value_column text_no_wrap"></th>
                              </tr>
                            </thead>

                            <tbody>
                              {materialListData?.data?.map((orderItem, index) => {
                                const isActive = orderItem?.id === activeOrderItem?.id;

                                return (
                                  <React.Fragment>
                                    <tr
                                      key={`orderItem_${orderItem?.id}_${index}`}
                                      onClick={() => handleToggleItem(orderItem)}
                                      className={`part_row ${isActive ? "active" : ""}`}
                                    >
                                      <td
                                        style={{ width: "4rem" }}
                                        className="text-center toggle_column"
                                      >
                                        <i className={`bi bi-chevron-right ${isActive ? "toggled" : ""} data_toggle_icon`}></i>
                                      </td>

                                      <td
                                        style={{ width: "20%" }}
                                        className="text_no_wrap text_semi_bold"
                                      >
                                        {orderItem?.item_name}<small>({orderItem?.unit?.unit_name})</small>
                                      </td>

                                      <td
                                        style={{ width: "15%" }}
                                        className="text_no_wrap text-center text_semi_bold"
                                      >
                                        <span className="text_mild">
                                          {(orderItem?.header_data?.estimation_total ?? 0)?.toLocaleString("en-GB", {
                                            minimumFractionDigits: 1,
                                            maximumFractionDigits: 1,
                                          })}
                                        </span>
                                      </td>

                                      <td
                                        style={{ width: "15%" }}
                                        className="text_no_wrap text-center text_semi_bold"
                                      >
                                        {countLabelBody(orderItem?.header_data?.order_total || 0)}
                                      </td>

                                      <td
                                        style={{ width: "15%" }}
                                        className="text_no_wrap text-center text_semi_bold"
                                      >
                                        <span className="text_mild">
                                          {(orderItem?.header_data?.delivered_total ?? 0)?.toLocaleString("en-GB", {
                                            minimumFractionDigits: 1,
                                            maximumFractionDigits: 1,
                                          })}
                                        </span>
                                      </td>

                                      <td
                                        style={{ width: "15%" }}
                                        className="text_no_wrap text-center text_semi_bold"
                                      >
                                        <span className="text_mild">
                                          {(orderItem?.header_data?.onSite_total ?? 0)?.toLocaleString("en-GB", {
                                            minimumFractionDigits: 1,
                                            maximumFractionDigits: 1,
                                          })}
                                        </span>
                                      </td>

                                      <td
                                        style={{ width: "16%" }}
                                        className="text_no_wrap item_value_column"
                                      >
                                        {orderItemActionBody(orderItem)}
                                      </td>
                                    </tr>

                                    <AnimatePresence initial={false}>
                                      {isActive && (
                                        <tr className="part_item_row">
                                          <td
                                            colSpan={7}
                                            className="p-0"
                                            style={{ width: "100%" }}
                                          >
                                            <motion.div
                                              initial={{ height: 0, opacity: 0 }}
                                              animate={{ height: "auto", opacity: 1 }}
                                              exit={{ height: 0, opacity: 0 }}
                                              transition={{ duration: 0.3, ease: [0.3, 0, 0.2, 1], }}
                                              style={{ overflow: "hidden" }}
                                            >
                                              {orderItem?.order && orderItem?.order?.length > 0 ? (
                                                <table className="table pck_track_list_table">
                                                  {orderItem?.order?.map((order, index) => {
                                                    return (
                                                      <tr key={`order_${index}`} className="part_item_sub_row">
                                                        <td style={{ width: "4rem" }} className="toggle_column"></td>

                                                        <td
                                                          style={{ width: "20%" }}
                                                          className="text_semi_bold item_title_column"
                                                        >
                                                          {order?.order?.date
                                                            ? format(parseISO(order.order.date), "d, MMM yyyy")
                                                            : "---"}
                                                        </td>

                                                        <td
                                                          style={{ width: "15%" }}
                                                          className="text_no_wrap item_value_column"
                                                        ></td>

                                                        <td
                                                          style={{ width: "15%" }}
                                                          className="text_no_wrap item_value_column"
                                                        >
                                                          {countLabelBody(order?.order?.quantity || 0)}
                                                        </td>

                                                        <td
                                                          style={{ width: "15%" }}
                                                          className="text_no_wrap item_value_column"
                                                        ></td>

                                                        <td
                                                          style={{ width: "15%" }}
                                                          className="text_no_wrap item_value_column"
                                                        ></td>

                                                        <td
                                                          style={{ width: "16%" }}
                                                          className="text_no_wrap item_value_column"
                                                        >
                                                          {orderSubItemActionBody(order)}
                                                        </td>
                                                      </tr>
                                                    )
                                                  })}
                                                </table>
                                              ) : (
                                                <table className="table pck_track_list_table">
                                                  <tr className="part_item_sub_row">
                                                    <td className="text-center empty_data_column" colSpan={7}>
                                                      <i className="bi bi-exclamation-circle"></i>
                                                      No order items have been added, click the Add button to add an order item.
                                                    </td>
                                                  </tr>
                                                </table>
                                              )}
                                            </motion.div>
                                          </td>
                                        </tr>
                                      )}
                                    </AnimatePresence>
                                  </React.Fragment>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <NoDataCreated message={`No order(s) found.`} />
                    )}
                  </div>
                )}

                {itemViewState === "Delivered" && (
                  <div className="page_main_content_section p-sm-2 p-0">
                    {materialListData?.data && materialListData?.data?.length > 0 ? (
                      <div className="pck_track_list_area">
                        <div className="table-responsive pck_track_list_table_area">
                          <table className="table pck_track_list_table">
                            <thead>
                              <tr>
                                <th style={{ width: "4%" }} className="text_no_wrap toggle_column"></th>

                                <th style={{ width: "20%" }} className="text_no_wrap item_title_column">
                                  Item
                                </th>

                                <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                  Estimation
                                </th>

                                <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                  Order
                                </th>

                                <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                  Delivered
                                </th>

                                <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                  Onsite
                                </th>

                                <th style={{ width: "16%" }} className="text-center item_value_column text_no_wrap"></th>
                              </tr>
                            </thead>

                            <tbody>
                              {materialListData?.data?.map((deliveredItem, index) => {
                                const isActive = deliveredItem?.id === activeDeliveredItem?.id;

                                return (
                                  <React.Fragment>
                                    <tr
                                      key={`deliveredItem_${deliveredItem?.id}_${index}`}
                                      onClick={() => handleToggleItem(deliveredItem)}
                                      className={`part_row ${isActive ? "active" : ""}`}
                                    >
                                      <td
                                        style={{ width: "4rem" }}
                                        className="text-center toggle_column"
                                      >
                                        <i className={`bi bi-chevron-right ${isActive ? "toggled" : ""} data_toggle_icon`}></i>
                                      </td>

                                      <td
                                        style={{ width: "20%" }}
                                        className="text_no_wrap text_semi_bold"
                                      >
                                        {deliveredItem?.item_name}<small>({deliveredItem?.unit?.unit_name})</small>
                                      </td>

                                      <td
                                        style={{ width: "15%" }}
                                        className="text_no_wrap text-center text_semi_bold"
                                      >
                                        <span className="text_mild">
                                          {(deliveredItem?.header_data?.estimation_total ?? 0)?.toLocaleString("en-GB", {
                                            minimumFractionDigits: 1,
                                            maximumFractionDigits: 1,
                                          })}
                                        </span>
                                      </td>

                                      <td
                                        style={{ width: "15%" }}
                                        className="text_no_wrap text-center text_semi_bold"
                                      >
                                        <span className="text_mild">
                                          {(deliveredItem?.header_data?.order_total ?? 0)?.toLocaleString("en-GB", {
                                            minimumFractionDigits: 1,
                                            maximumFractionDigits: 1,
                                          })}
                                        </span>
                                      </td>

                                      <td
                                        style={{ width: "15%" }}
                                        className="text_no_wrap text-center text_semi_bold"
                                      >
                                        {countLabelBody(deliveredItem?.header_data?.delivered_total || 0)}
                                      </td>

                                      <td
                                        style={{ width: "15%" }}
                                        className="text_no_wrap text-center text_semi_bold"
                                      >
                                        <span className="text_mild">
                                          {(deliveredItem?.header_data?.onSite_total ?? 0)?.toLocaleString("en-GB", {
                                            minimumFractionDigits: 1,
                                            maximumFractionDigits: 1,
                                          })}
                                        </span>
                                      </td>

                                      <td
                                        style={{ width: "16%" }}
                                        className="text_no_wrap item_value_column"
                                      >
                                        {deliveredItemActionBody(deliveredItem)}
                                      </td>
                                    </tr>

                                    <AnimatePresence initial={false}>
                                      {isActive && (
                                        <tr className="part_item_row">
                                          <td
                                            colSpan={7}
                                            className="p-0"
                                            style={{ width: "100%" }}
                                          >
                                            <motion.div
                                              initial={{ height: 0, opacity: 0 }}
                                              animate={{ height: "auto", opacity: 1 }}
                                              exit={{ height: 0, opacity: 0 }}
                                              transition={{ duration: 0.3, ease: [0.3, 0, 0.2, 1], }}
                                              style={{ overflow: "hidden" }}
                                            >
                                              {deliveredItem?.delivered && deliveredItem?.delivered?.length > 0 ? (
                                                <table className="table pck_track_list_table">
                                                  {deliveredItem?.delivered?.map((delivered, index) => {
                                                    return (
                                                      <tr key={`delivered_${index}`} className="part_item_sub_row">
                                                        <td style={{ width: "4rem" }} className="toggle_column"></td>

                                                        <td
                                                          style={{ width: "20%" }}
                                                          className="text_semi_bold item_title_column"
                                                        >
                                                          {delivered?.delivered?.date
                                                            ? format(parseISO(delivered?.delivered?.date), "d, MMM yyyy")
                                                            : "---"}
                                                        </td>

                                                        <td
                                                          style={{ width: "15%" }}
                                                          className="text_no_wrap item_value_column"
                                                        ></td>

                                                        <td
                                                          style={{ width: "15%" }}
                                                          className="text_no_wrap item_value_column"
                                                        ></td>

                                                        <td
                                                          style={{ width: "15%" }}
                                                          className="text_no_wrap item_value_column"
                                                        >
                                                          {countLabelBody(delivered?.delivered?.delivered_quantity || 0)}
                                                        </td>

                                                        <td
                                                          style={{ width: "15%" }}
                                                          className="text_no_wrap item_value_column"
                                                        ></td>

                                                        <td
                                                          style={{ width: "16%", minWidth: '130px' }}
                                                          className="text_no_wrap item_value_column"
                                                        >
                                                          {deliveredSubItemActionBody(delivered)}
                                                        </td>
                                                      </tr>
                                                    )
                                                  })}
                                                </table>
                                              ) : (
                                                <table className="table pck_track_list_table">
                                                  <tr className="part_item_sub_row">
                                                    <td className="text-center empty_data_column" colSpan={7}>
                                                      <i className="bi bi-exclamation-circle"></i>
                                                      No delivered items have been added, click the Add button to add a delivered item.
                                                    </td>
                                                  </tr>
                                                </table>
                                              )}
                                            </motion.div>
                                          </td>
                                        </tr>
                                      )}
                                    </AnimatePresence>
                                  </React.Fragment>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <NoDataCreated message={`No delivered items found.`} />
                    )}
                  </div>
                )}

                {itemViewState === "Onsite" && (
                  <div className="page_main_content_section p-sm-2 p-0">
                    {materialListData?.data && materialListData?.data?.length > 0 ? (
                      <div className="pck_track_list_area">
                        <div className="table-responsive pck_track_list_table_area">
                          <table className="table pck_track_list_table">
                            <thead>
                              <tr>
                                <th style={{ width: "4%" }} className="text_no_wrap toggle_column"></th>

                                <th style={{ width: "20%" }} className="text_no_wrap item_title_column">
                                  Item
                                </th>

                                <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                  Estimation
                                </th>

                                <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                  Order
                                </th>

                                <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                  Delivered
                                </th>

                                <th style={{ width: "15%" }} className="text-center item_value_column text_no_wrap">
                                  Onsite
                                </th>

                                <th style={{ width: "16%" }} className="text-center item_value_column text_no_wrap"></th>
                              </tr>
                            </thead>

                            <tbody>
                              {materialListData?.data?.map((onsiteItem, index) => {
                                const isActive = onsiteItem?.id === activeOnsiteItem?.id;

                                return (
                                  <React.Fragment>
                                    <tr
                                      key={`onsiteItem_${onsiteItem?.id}_${index}`}
                                      onClick={() => handleToggleItem(onsiteItem)}
                                      className={`part_row ${isActive ? "active" : ""}`}
                                    >
                                      <td
                                        style={{ width: "4rem" }}
                                        className="text-center toggle_column"
                                      >
                                        <i className={`bi bi-chevron-right ${isActive ? "toggled" : ""} data_toggle_icon`}></i>
                                      </td>

                                      <td
                                        style={{ width: "20%" }}
                                        className="text_no_wrap text_semi_bold"
                                      >
                                        {onsiteItem?.item_name}<small>({onsiteItem?.unit?.unit_name})</small>
                                      </td>

                                      <td
                                        style={{ width: "15%" }}
                                        className="text_no_wrap text-center text_semi_bold"
                                      >
                                        <span className="text_mild">
                                          {(onsiteItem?.header_data?.estimation_total ?? 0)?.toLocaleString("en-GB", {
                                            minimumFractionDigits: 1,
                                            maximumFractionDigits: 1,
                                          })}
                                        </span>
                                      </td>

                                      <td
                                        style={{ width: "15%" }}
                                        className="text_no_wrap text-center text_semi_bold"
                                      >
                                        <span className="text_mild">
                                          {(onsiteItem?.header_data?.order_total ?? 0)?.toLocaleString("en-GB", {
                                            minimumFractionDigits: 1,
                                            maximumFractionDigits: 1,
                                          })}
                                        </span>
                                      </td>

                                      <td
                                        style={{ width: "15%" }}
                                        className="text_no_wrap text-center text_semi_bold"
                                      >
                                        <span className="text_mild">
                                          {(onsiteItem?.header_data?.delivered_total ?? 0)?.toLocaleString("en-GB", {
                                            minimumFractionDigits: 1,
                                            maximumFractionDigits: 1,
                                          })}
                                        </span>
                                      </td>

                                      <td
                                        style={{ width: "15%" }}
                                        className="text_no_wrap text-center text_semi_bold"
                                      >
                                        {countLabelBody(onsiteItem?.header_data?.onSite_total || 0)}
                                      </td>

                                      <td
                                        style={{ width: "16%" }}
                                        className="text_no_wrap item_value_column"
                                      >
                                        {onsiteItemActionBody(onsiteItem)}
                                      </td>
                                    </tr>

                                    <AnimatePresence initial={false}>
                                      {isActive && (
                                        <tr className="part_item_row">
                                          <td
                                            colSpan={7}
                                            className="p-0"
                                            style={{ width: "100%" }}
                                          >
                                            <motion.div
                                              initial={{ height: 0, opacity: 0 }}
                                              animate={{ height: "auto", opacity: 1 }}
                                              exit={{ height: 0, opacity: 0 }}
                                              transition={{ duration: 0.3, ease: [0.3, 0, 0.2, 1], }}
                                              style={{ overflow: "hidden" }}
                                            >
                                              {onsiteItem?.onsite && onsiteItem?.onsite?.length > 0 ? (
                                                <table className="table pck_track_list_table">
                                                  {onsiteItem?.onsite?.map((onsite, index) => {
                                                    return (
                                                      <tr key={`onsite_${index}`} className="part_item_sub_row">
                                                        <td style={{ width: "4rem" }} className="toggle_column"></td>

                                                        <td
                                                          style={{ width: "20%" }}
                                                          className="text_semi_bold item_title_column"
                                                        >
                                                          {onsite?.onsite?.date
                                                            ? format(parseISO(onsite?.onsite?.date), "d, MMM yyyy")
                                                            : "---"}
                                                        </td>

                                                        <td
                                                          style={{ width: "15%" }}
                                                          className="text_no_wrap item_value_column"
                                                        ></td>

                                                        <td
                                                          style={{ width: "15%" }}
                                                          className="text_no_wrap item_value_column"
                                                        ></td>

                                                        <td
                                                          style={{ width: "15%" }}
                                                          className="text_no_wrap item_value_column"
                                                        ></td>

                                                        <td
                                                          style={{ width: "15%" }}
                                                          className="text_no_wrap item_value_column"
                                                        >
                                                          {countLabelBody(onsite?.onsite?.onsite_quantity || 0)}
                                                        </td>

                                                        <td
                                                          style={{ width: "16%", minWidth: '130px' }}
                                                          className="text_no_wrap item_value_column"
                                                        >
                                                          {onsiteSubItemActionBody(onsite)}
                                                        </td>
                                                      </tr>
                                                    )
                                                  })}
                                                </table>
                                              ) : (
                                                <table className="table pck_track_list_table">
                                                  <tr className="part_item_sub_row">
                                                    <td className="text-center empty_data_column" colSpan={7}>
                                                      <i className="bi bi-exclamation-circle"></i>
                                                      No onsite items have been added, click the Add button to add a onsite item.
                                                    </td>
                                                  </tr>
                                                </table>
                                              )}
                                            </motion.div>
                                          </td>
                                        </tr>
                                      )}
                                    </AnimatePresence>
                                  </React.Fragment>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <NoDataCreated message={`No onsite items found.`} />
                    )}
                  </div>
                )}
              </React.Fragment>
            )}
          </div>
        )}
      </div>

      <Dialog
        visible={showPurchaseOrdersModal}
        headerClassName="custom_modal_header"
        header={(
          <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
              Purchase Orders
              <small className="modal_sub_title d-block">
                Grouped by merchant
              </small>
            </h5>
            <button
              type="button"
              className="close_modal_btn p-ripple"
              onClick={() => setShowPurchaseOrdersModal(false)}
            >
              <i className="bi bi-x-circle"></i>
              <Ripple />
            </button>
          </div>
        )}
        className="custom_modal_dialog material_purchase_order_dialog"
        onHide={() => setShowPurchaseOrdersModal(false)}
        dismissableMask
        draggable={false}
      >
        <div className="custom_modal_body material_purchase_order_body">
          <div className="material_purchase_order_list">
            {purchaseOrders.map((purchaseOrder) => {
              const merchantName = purchaseOrder.merchant?.merchant || "Merchant";
              const isDownloading = downloadingMerchantId === purchaseOrder.merchant_id;

              return (
                <section
                  className="material_purchase_order_card"
                  key={`purchase_order_${purchaseOrder.merchant_id}`}
                >
                  <div className="purchase_order_merchant_header">
                    <div className="purchase_order_merchant_identity">
                      <span className="purchase_order_shop_icon">
                        <i className="bi bi-shop"></i>
                      </span>
                      <div>
                        <h6>{merchantName}</h6>
                        <p>
                          {purchaseOrder.merchant?.phone_no || "Phone not available"}
                          {purchaseOrder.merchant?.address && (
                            <React.Fragment>
                              <span className="purchase_order_meta_dot">&bull;</span>
                              {purchaseOrder.merchant.address}
                            </React.Fragment>
                          )}
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="custom_btn primary purchase_order_download_btn p-ripple"
                      onClick={() => handleDownloadPurchaseOrder(purchaseOrder)}
                      disabled={isDownloading}
                    >
                      <i className={`bi ${isDownloading ? "bi-arrow-repeat purchase_order_spin" : "bi-download"}`}></i>
                      <span>{isDownloading ? "Downloading" : "Download"}</span>
                      <Ripple />
                    </button>
                  </div>

                  <div className="table-responsive purchase_order_table_area">
                    <table className="table purchase_order_table mb-0">
                      <thead>
                        <tr>
                          <th>Material</th>
                          <th>Manufacturer</th>
                          <th className="text-end">Quantity</th>
                          <th>Unit</th>
                          <th className="text-end">Unit price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchaseOrder.material_list.map((material) => {
                          const bankData = material.material_bank_data;
                          const unitPrice = Number(
                            bankData?.trade_price_per_unit ??
                            bankData?.standard_price_per_unit ??
                            0
                          );

                          return (
                            <tr key={`purchase_order_material_${material.id}`}>
                              <td className="text_semi_bold">
                                {material.material_name || bankData?.material?.material_name || "---"}
                              </td>
                              <td>{bankData?.manufacturer?.manufacturer || "---"}</td>
                              <td className="text-end text_no_wrap">
                                {Number(material.quantity || 0).toLocaleString("en-GB")}
                              </td>
                              <td>{bankData?.measurement?.measurement || "---"}</td>
                              <td className="text-end text_no_wrap">
                                {currencySymbol}&nbsp;
                                {unitPrice.toLocaleString("en-GB", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="purchase_order_card_footer">
                    <span>
                      {purchaseOrder.material_list.length} material(s)
                    </span>
                    <strong>
                      Total: {currencySymbol}&nbsp;
                      {getPurchaseOrderTotal(purchaseOrder).toLocaleString("en-GB", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </Dialog>

      {/* Material item modal */}
      <Dialog
        visible={showMaterialItemModal}
        headerClassName="custom_modal_header"
        header={materialItemModalHeader}
        footer={materialItemModalFooter}
        dismissableMask
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseMaterialItemModal}
        draggable={false}
      >
        <div className="custom_modal_body">
          <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
            <div className="row">
              {/* Item name */}
              <div className="col-12">
                <TextInput
                  key={`itemName`}
                  id="itemName"
                  label="Item name"
                  labelHtmlFor="itemName"
                  required={true}
                  value={itemName}
                  placeholder="Enter item name"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setItemName(e.target.value)}
                  error={itemNameError}
                />
              </div>

              {/* Unit */}
              <div className="col-12 col-sm-6">
                <div className="page_form_group">
                  <label
                    htmlFor="unit"
                    className="custom_form_label is_required"
                  >
                    Unit
                  </label>
                  <Dropdown
                    key={`unit`}
                    id="unit"
                    value={materialUnit}
                    options={materialDataUnitsList}
                    onChange={handleUnitChange}
                    placeholder="Select unit"
                    className="form_dropdown w-100"
                    optionLabel="unit_name"
                    optionValue="id"
                    filter
                    filterPlaceholder="Search here"
                    showClear
                  />

                  {materialUnitError && (
                    <small className="form_error_msg">
                      {materialUnitError}
                    </small>
                  )}
                </div>
              </div>

              {/* Quantity */}
              <div className="col-12 col-sm-6">
                <NumberInput
                  key={`quantity`}
                  inputId="quantity"
                  label="Quantity"
                  labelHtmlFor="quantity"
                  placeholder="Enter quantity"
                  onChange={handleQuantityChange}
                  value={quantity}
                  required={true}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                  error={quantityError}
                />
              </div>

              {/* Unit price */}
              <div className="col-12 col-sm-6">
                <NumberInput
                  key={`unitPrice`}
                  inputId="unitPrice"
                  label="Unit price"
                  labelHtmlFor="unitPrice"
                  placeholder="Enter price per unit"
                  onChange={handleUnitPriceChange}
                  value={unitPrice}
                  required={true}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  prefix={currencySymbol + " "}
                  min={0}
                  error={unitPriceError}
                  formGroupClassName="mb-2"
                />

                <div className="page_form_group">
                  <div className="response_state">
                    <Checkbox
                      inputId="isIncludeTax"
                      name="isIncludeTax"
                      onChange={handleCheckIncludeTax}
                      checked={isIncludeTax}
                    />
                    <label htmlFor="isIncludeTax">Include tax ({taxPercentage}%)</label>
                  </div>
                </div>
              </div>

              {/* Sub total */}
              <div className="col-12 col-sm-6">
                <NumberInput
                  key={`subTotal`}
                  inputId="subTotal"
                  label="Sub total"
                  labelHtmlFor="subTotal"
                  placeholder="Enter sub total"
                  onChange={handleSubTotalChange}
                  value={subTotal}
                  required={false}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  prefix={currencySymbol + " "}
                  min={0}
                  error={subTotalError}
                  inputDisabled={true}
                />
              </div>

              {/* Note */}
              <div className="col-12">
                <Textarea
                  id="note"
                  label="Note"
                  labelHtmlFor="note"
                  required={false}
                  value={note}
                  placeholder="Note"
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
                  error={noteError}
                  formGroupClassName="mb-2"
                />
              </div>
            </div>
          </div>
        </div>
      </Dialog>
      {/*  */}


      {/* Order modal */}
      <Dialog
        visible={showOrderModal}
        headerClassName="custom_modal_header"
        header={orderModalHeader}
        footer={orderModalFooter}
        dismissableMask
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseOrderModal}
        draggable={false}
      >
        <div className="custom_modal_body">
          <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
            <div className="row">
              {/* Item */}
              {activeOrderItem && activeOrderItem !== null && (
                <div className="col-12">
                  <div className="page_form_group">
                    <h6 className="data_label">
                      <span className="text_bold">
                        <span className="text-dark">Item:&nbsp;</span>
                        {activeOrderItem?.item_name}</span>&nbsp;<small>({activeOrderItem?.unit?.unit_name})</small>
                    </h6>
                  </div>
                </div>
              )}

              {dataState === "Add" && (
                <div className="col-12">
                  {selectedDeal && (
                    <div className="selected_material_deal">
                      <span><i className="bi bi-check-circle-fill"></i> {selectedDeal.merchant?.merchant}</span>
                      <strong>{currencySymbol} {Number(selectedDeal.trade_price_per_unit).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</strong>
                    </div>
                  )}
                  <button type="button" className="find_best_deal_btn p-ripple" onClick={handleFindBestDeal}>
                    <i className={`bi ${selectedDeal ? "bi-arrow-repeat" : "bi-tags-fill"}`}></i>
                    {selectedDeal ? "Change item" : "Click to find the best deal"}
                    <Ripple />
                  </button>
                </div>
              )}

              {/* Order quantity */}
              <div className="col-12 col-sm-6">
                <NumberInput
                  key={`orderQuantity`}
                  inputId="orderQuantity"
                  label="Quantity"
                  labelHtmlFor="orderQuantity"
                  placeholder="Enter quantity"
                  onChange={handleOrderQuantityChange}
                  value={orderQuantity}
                  required={true}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                  error={orderQuantityError}
                />
              </div>

              {/* Order unit price */}
              <div className="col-12 col-sm-6">
                <NumberInput
                  key={`orderUnitPrice`}
                  inputId="orderUnitPrice"
                  label="Unit price"
                  labelHtmlFor="orderUnitPrice"
                  placeholder="Enter unit price"
                  onChange={handleOrderUnitPriceChange}
                  value={orderUnitPrice}
                  required={true}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  prefix={currencySymbol + " "}
                  min={0}
                  error={orderUnitPriceError}
                />
              </div>

              {/* Order date */}
              <div className="col-12 col-sm-6">
                <DatePicker
                  key={`orderDate`}
                  id="orderDate"
                  label="Date"
                  labelHtmlFor="orderDate"
                  required={true}
                  value={orderDate}
                  placeholder="dd/mm/yyyy"
                  onChange={setOrderDate}
                  error={orderDateError}
                  showClear
                  onClear={() => setOrderDate(null)}
                />
              </div>

              {/* Last unit price */}
              <div className="col-12">
                <div className="data_value_label">
                  <h6>Last unit price:</h6>
                  <h6>
                    {currencySymbol}&nbsp;{orderUnitLastPrice?.toLocaleString("en-GB", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
      {/*  */}

      <Dialog
        visible={showBestDealModal}
        headerClassName="custom_modal_header"
        header={(
          <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">Best deals <small className="modal_sub_title d-block">{activeOrderItem?.item_name}</small></h5>
            <button type="button" className="close_modal_btn p-ripple" onClick={() => setShowBestDealModal(false)}><i className="bi bi-x-circle"></i><Ripple /></button>
          </div>
        )}
        className="custom_modal_dialog material_deal_dialog"
        onHide={() => setShowBestDealModal(false)}
        dismissableMask
        draggable={false}
      >
        <div className="custom_modal_body material_deal_body">
          <div className="deal_filter_bar">
            <span className="deal_search_box">
              <i className="bi bi-search"></i>
              <InputText value={dealSearch} onChange={(e) => setDealSearch(e.target.value)} placeholder="Search deals" />
            </span>
            <Dropdown value={selectedDealShop} onChange={(e) => setSelectedDealShop(e.value)} options={dealShopOptions} placeholder="All shops" showClear />
            <Dropdown value={selectedDealBrand} onChange={(e) => setSelectedDealBrand(e.value)} options={dealBrandOptions} placeholder="All brands" showClear />
          </div>

          {dealLoading ? (
            <div className="deal_empty_state"><i className="bi bi-arrow-repeat deal_spin"></i><span>Finding the best deals...</span></div>
          ) : filteredMaterialDeals.length === 0 ? (
            <div className="deal_empty_state"><i className="bi bi-tag"></i><span>No matching deals found.</span></div>
          ) : (
            <div className="material_deal_list">
              {filteredMaterialDeals.map((deal) => {
                const expanded = expandedDealId === deal.id;
                return (
                  <div className={`material_deal_card ${selectedDeal?.id === deal.id ? "selected" : ""}`} key={deal.id}>
                    <div className="deal_card_main">
                      <div className="deal_shop_icon"><i className="bi bi-shop"></i></div>
                      <div className="deal_card_info">
                        <h6>{deal.merchant?.merchant || "Merchant"}</h6>
                        <p>{deal.manufacturer?.manufacturer || "Brand not available"} · {deal.measurement?.measurement}</p>
                        <button type="button" className="deal_expand_btn" onClick={() => setExpandedDealId(expanded ? null : deal.id)}>{expanded ? "Hide details" : "View details"} <i className={`bi bi-chevron-${expanded ? "up" : "down"}`}></i></button>
                      </div>
                      <div className="deal_price_area"><small>Trade price</small><strong>{currencySymbol} {Number(deal.trade_price_per_unit).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</strong><span>per {deal.measurement?.measurement || "unit"}</span></div>
                      <Button label={selectedDeal?.id === deal.id ? "Selected" : "Select"} icon={selectedDeal?.id === deal.id ? "bi bi-check" : undefined} className="custom_btn primary deal_select_btn" onClick={() => handleSelectDeal(deal)} />
                    </div>
                    {expanded && (
                      <div className="deal_card_details">
                        <div><span>Standard price</span><strong>{currencySymbol} {deal.standard_price_per_unit}</strong></div>
                        <div><span>VAT per unit</span><strong>{currencySymbol} {deal.vat_per_unit}</strong></div>
                        <div><span>Updated</span><strong>{deal.updated_date || "—"}</strong></div>
                        <div><span>Dimensions (T × W × H)</span><strong>{[deal.thikness, deal.width, deal.hight].map(v => v || "—").join(" × ")}</strong></div>
                        <div><span>Shop phone</span><strong>{deal.merchant?.phone_no || "—"}</strong></div>
                        <div><span>Shop address</span><strong>{deal.merchant?.address || "—"}</strong></div>
                        <div><span>Manufacturer phone</span><strong>{deal.manufacturer?.phone_no || "—"}</strong></div>
                        <div><span>Manufacturer address</span><strong>{deal.manufacturer?.address || "—"}</strong></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Dialog>


      {/* Delivery modal */}
      <Dialog
        visible={showDeliveryModal}
        headerClassName="custom_modal_header"
        header={deliveryModalHeader}
        footer={deliveryModalFooter}
        dismissableMask
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseDeliveryModal}
        draggable={false}
      >
        <div className="custom_modal_body">
          <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
            <div className="row">
              {/* Item */}
              {activeDeliveredItem && activeDeliveredItem !== null && (
                <div className="col-12">
                  <div className="page_form_group">
                    <h6 className="data_label">
                      <span className="text_bold">
                        <span className="text-dark">Item:&nbsp;</span>
                        {activeDeliveredItem?.item_name}</span>&nbsp;<small>({activeDeliveredItem?.unit?.unit_name})</small>
                    </h6>
                  </div>
                </div>
              )}

              {/* Delivery Quantity */}
              <div className="col-12 col-sm-6">
                <NumberInput
                  key={`deliveryQuantity`}
                  inputId="deliveryQuantity"
                  label="Quantity"
                  labelHtmlFor="deliveryQuantity"
                  placeholder="Enter quantity"
                  onChange={handleDeliveryQuantityChange}
                  value={deliveryQuantity}
                  required={true}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                  error={deliveryQuantityError}
                  formGroupClassName="mb-sm-2"
                />
              </div>

              {/* Delivery date */}
              <div className="col-12 col-sm-6">
                <DatePicker
                  key={`deliveryDate`}
                  id="deliveryDate"
                  label="Date"
                  labelHtmlFor="deliveryDate"
                  required={true}
                  value={deliveryDate}
                  placeholder="dd/mm/yyyy"
                  onChange={setDeliveryDate}
                  error={deliveryDateError}
                  showClear
                  onClear={() => setDeliveryDate(null)}
                  formGroupClassName="mb-2 mb-sm-2"
                />
              </div>
            </div>
          </div>
        </div>
      </Dialog>
      {/*  */}


      {/* Onsite modal */}
      <Dialog
        visible={showOnsiteModal}
        headerClassName="custom_modal_header"
        header={onsiteModalHeader}
        footer={onsiteModalFooter}
        dismissableMask
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseOnsiteModal}
        draggable={false}
      >
        <div className="custom_modal_body">
          <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
            <div className="row">
              {/* Item */}
              {activeOnsiteItem && activeOnsiteItem !== null && (
                <div className="col-12">
                  <div className="page_form_group">
                    <h6 className="data_label">
                      <span className="text_bold">
                        <span className="text-dark">Item:&nbsp;</span>
                        {activeOnsiteItem?.item_name}</span>&nbsp;<small>({activeOnsiteItem?.unit?.unit_name})</small>
                    </h6>
                  </div>
                </div>
              )}

              {/* Onsite Quantity */}
              <div className="col-12 col-sm-6">
                <NumberInput
                  key={`onsiteQuantity`}
                  inputId="onsiteQuantity"
                  label="Quantity"
                  labelHtmlFor="onsiteQuantity"
                  placeholder="Enter quantity"
                  onChange={handleOnsiteQuantityChange}
                  value={onsiteQuantity}
                  required={true}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                  error={onsiteQuantityError}
                  formGroupClassName="mb-sm-2"
                />
              </div>

              {/* Onsite date */}
              <div className="col-12 col-sm-6">
                <DatePicker
                  key={`onsiteDate`}
                  id="onsiteDate"
                  label="Date"
                  labelHtmlFor="onsiteDate"
                  required={true}
                  value={onsiteDate}
                  placeholder="dd/mm/yyyy"
                  onChange={setOnsiteDate}
                  error={onsiteDateError}
                  showClear
                  onClear={() => setOnsiteDate(null)}
                  formGroupClassName="mb-2 mb-sm-2"
                />
              </div>
            </div>
          </div>
        </div>
      </Dialog>
      {/*  */}


      {/* Item note modal */}
      <Dialog
        visible={showNoteViewModal}
        header={noteViewModalHeader}
        footer={noteViewModalFooter}
        headerClassName="custom_modal_header"
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseNoteViewModal}
        dismissableMask
        draggable={false}
      >
        <div className="custom_modal_body">
          <div className="p-1 w-100">
            <div className="row">
              {selectedItemNote && selectedItemNote !== "" ? (
                <div className="col-12">
                  {/* Note */}
                  <div className="col-12">
                    <div className="note_view_container">
                      <p>{selectedItemNote}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="col-12">
                  <div
                    className="no_records_found"
                    style={{ minHeight: "15vh" }}
                  >
                    <i className="bi bi-exclamation-circle me-1"></i>
                    No information available.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Dialog>
      {/*  */}
    </React.Fragment >
  );
};

export default MaterialItems;
